import { MapPin, Phone, Mail, Clock } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ConsultationForm from "@/components/ConsultationForm";
import { useLanguage } from "@/hooks/useLanguage";
import SEO from "@/components/SEO";
import { organizationSchema } from "@/lib/seo-schemas";

const Contact = () => {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen">
      <SEO 
        title="Contact Us | Daniel Albert Law Firm Houston"
        description="Contact our Houston law firm for immigration, criminal defense, family law & more. Trilingual services. Call (832) 930-3059. Free consultation available."
        keywords="contact law firm Houston, Houston attorney, immigration lawyer contact, legal consultation Houston"
        schema={organizationSchema}
      />
      <Header />

      {/* Hero Section */}
      <section className="py-20 bg-gradient-hero text-primary-foreground">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">{t('contact.title')}</h1>
            <p className="text-xl opacity-95">
              {t('contact.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold mb-8">{t('contact.getintouch')}</h2>
              
              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-6 w-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">{t('contact.office')}</h3>
                    <p className="text-muted-foreground">
                      10925 Beechnut St A106<br />
                      Houston, TX 77072<br />
                      United States
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="h-6 w-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">{t('contact.phone')}</h3>
                    <p className="text-muted-foreground">
                      <strong>English:</strong>{" "}
                      <a href="tel:8329303059" className="text-accent hover:underline">
                        (832) 930-3059
                      </a>
                      <br />
                      <strong>Español:</strong>{" "}
                      <a href="tel:8323567254" className="text-accent hover:underline">
                        (832) 356-7254
                      </a>
                      <br />
                      <strong>Tiếng Việt:</strong>{" "}
                      <a href="tel:8329303059" className="text-accent hover:underline">
                        (832) 930-3059
                      </a>
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">{t('contact.email')}</h3>
                    <p className="text-muted-foreground">
                      <a href="mailto:info@danielalbertlaw.com" className="text-accent hover:underline">
                        info@danielalbertlaw.com
                      </a>
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="h-6 w-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">{t('contact.hours')}</h3>
                    <p className="text-muted-foreground">
                      {t('contact.hours.mon')}<br />
                      {t('contact.hours.closed')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="rounded-lg overflow-hidden shadow-lg">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3467.2838429!2d-95.5867!3d29.6567!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8640e3b1f9f9f9f9%3A0x1234567890abcdef!2s10925%20Beechnut%20St%2C%20Houston%2C%20TX%2077072!5e0!3m2!1sen!2sus!4v1234567890123"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Daniel Albert Law Firm Location"
                ></iframe>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <ConsultationForm />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
